import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { giphyApi } from '@/lib/api';

interface GifPickerProps {
  anchorRef: React.RefObject<HTMLDivElement>;
  onSelect: (gifUrl: string) => void;
}

export function GifPicker({ anchorRef, onSelect }: GifPickerProps) {
  const pickerRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ bottom: number; left: number } | null>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [gifs, setGifs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Position above anchor, bottom edge attached to the top of the icon button
  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setCoords({
      bottom: window.innerHeight - rect.top + 4,
      left: rect.left,
    });
  }, [anchorRef]);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        pickerRef.current && !pickerRef.current.contains(event.target as Node) &&
        anchorRef.current && !anchorRef.current.contains(event.target as Node)
      ) {
        onSelect('__close__');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onSelect, anchorRef]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Fetch GIFs
  const fetchGifs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = debouncedQuery
        ? await giphyApi.search(debouncedQuery)
        : await giphyApi.trending();
      setGifs(res.data || []);
    } catch {
      setError('Failed to load GIFs');
      setGifs([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery]);

  useEffect(() => {
    fetchGifs();
  }, [fetchGifs]);

  if (!coords) return null;

  return createPortal(
    <div
      ref={pickerRef}
      style={{ position: 'fixed', bottom: coords.bottom, left: coords.left, zIndex: 9999 }}
      className="bg-white rounded-2xl shadow-xl border border-grey-200 p-3 w-[320px]"
    >
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search GIFs..."
        className="w-full px-3 py-1.5 text-sm border border-grey-200 rounded-lg mb-2 focus:outline-none focus:border-accent"
        autoFocus
      />

      <div className="h-[280px] overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-grey-400" />
          </div>
        )}

        {error && !loading && (
          <div className="flex items-center justify-center h-full text-sm text-grey-500">
            {error}
          </div>
        )}

        {!loading && !error && gifs.length === 0 && (
          <div className="flex items-center justify-center h-full text-sm text-grey-500">
            No GIFs found
          </div>
        )}

        {!loading && !error && gifs.length > 0 && (
          <div className="grid grid-cols-2 gap-1">
            {gifs.map((gif: any) => (
              <button
                key={gif.id}
                type="button"
                onClick={() => onSelect(gif.images.fixed_height.url)}
                className="rounded-lg overflow-hidden hover:opacity-80 transition-opacity"
              >
                <img
                  src={gif.images.fixed_height_small.url}
                  alt={gif.title}
                  className="w-full h-auto"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-2 text-center text-[10px] text-grey-400">
        Powered by GIPHY
      </div>
    </div>,
    document.body,
  );
}
